import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import CopyButton from "@components/buttons/CopyButton";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import { analyticsApis } from "@services/apis/Analytics";
import {
  GetMerchantSuccessRatePayload,
  GetMerchantSuccessRateResponseData,
} from "@services/models/Analytics/GetMerchantSuccessRate";
import {
  GetSystemSuccessRatePayload,
  GetSystemSuccessRateResponseData,
} from "@services/models/Analytics/GetSystemSuccessRate";
import {
  GetUpiSuccessRatePayload,
  GetUpiSuccessRateResponseData,
} from "@services/models/Analytics/GetUpiSuccessRate";
import { ColDef } from "ag-grid-community";
import { useEffect, useMemo, useRef, useState } from "react";
import { isPaysonicHost } from "src/config/deploymentConfig";
import ConstantFunctions from "../../../utils/constants_functions";
import { CSV_EXPORT_CONSTANTS } from "../../../utils/csvExportConstants";
import MerchantSuccessRateChart from "../../analytics/MerchantSuccessRateChart";
import SystemSuccessRateChart from "../../analytics/SystemSuccessRateChart";
import UpiSuccessRateChart from "../../analytics/UpiSuccessRateChart";

const MobileAnalytics = () => {
  const constantFunctions = new ConstantFunctions();
  const isPaysonic = isPaysonicHost();

  const [systemSuccessRateData, setSystemSuccessRateData] =
    useState<GetSystemSuccessRateResponseData[]>();
  const [merchantSuccessRateData, setMerchantSuccessRateData] =
    useState<GetMerchantSuccessRateResponseData[]>();
  const [upiSuccessRateData, setUpiSuccessRateData] =
    useState<GetUpiSuccessRateResponseData[]>();
  const [error, setError] = useState<String>("");
  const [isLoading, setIsLoading] = useState<Boolean>(false);

  const [systemExportLoading, setSystemExportLoading] =
    useState<boolean>(false);
  const [merchantExportLoading, setMerchantExportLoading] =
    useState<boolean>(false);
  const [upiExportLoading, setUpiExportLoading] = useState<boolean>(false);

  const [selectedGraph, setSelectedGraph] = useState<
    "system" | "merchant" | "upi"
  >("system");

  const [dailyOrHourlySystemSuccessRate, setDailyOrHourlySystemSuccessRate] =
    useState<string>("Daily");
  const [
    dailyOrHourlyMerchantSuccessRate,
    setDailyOrHourlyMerchantSuccessRate,
  ] = useState<string>("Daily");
  const [dailyOrHourlyUpiSuccessRate, setDailyOrHourlyUpiSuccessRate] =
    useState<string>("Daily");

  const [merchantSearchTerm, setMerchantSearchTerm] = useState<string>("");
  const [debouncedMerchantSearchTerm, setDebouncedMerchantSearchTerm] =
    useState<string>("");

  const [upiSearchTerm, setUpiSearchTerm] = useState<string>("");
  const [debouncedUpiSearchTerm, setDebouncedUpiSearchTerm] =
    useState<string>("");

  const rowsPerPage = 10000;

  const systemColumnDefs: ColDef[] = useMemo(() => {
    const columns: ColDef[] = [
      {
        headerName: "Date",
        field: "slot",
        flex: 0.7,
        minWidth: 70,
        maxWidth: 100,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agDateColumnFilter",
        valueFormatter: (params) =>
          constantFunctions.dateFormatInDDMMYY(params.value ?? ""),
      },
      {
        headerName: "TPV USD",
        field: "TV_USD_Success",
        flex: 0.9,
        minWidth: 90,
        maxWidth: 120,
        valueFormatter: (params) =>
          constantFunctions.formatCurrency(Number(params.value)),
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Success %",
        field: "successRatePercent",
        flex: 1.0,
        minWidth: 100,
        maxWidth: 130,
        valueFormatter: (params) => `${params.value}%`,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "# Trades",
        field: "totalTrades",
        flex: 0.9,
        minWidth: 90,
        maxWidth: 120,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "In-Progress",
        field: "inProgressCount",
        flex: 1.0,
        minWidth: 100,
        maxWidth: 130,
        valueFormatter: (params) => params.value ?? 0,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
    ];

    return isPaysonic
      ? columns.filter((col) => col.field !== "TV_USD_Success")
      : columns;
  }, [constantFunctions, isPaysonic]);

  const merchantColumnDefs: ColDef[] = useMemo(() => {
    const columns: ColDef[] = [
      {
        headerName: "Date",
        field: "slot",
        flex: 0.7,
        minWidth: 70,
        maxWidth: 100,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agDateColumnFilter",
        valueFormatter: (params) =>
          constantFunctions.dateFormatInDDMMYY(params.value ?? ""),
      },
      {
        headerName: "Merchant",
        field: "merchantName",
        flex: 0.9,
        minWidth: 90,
        maxWidth: 140,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
        },
        valueFormatter: (params) =>
          isPaysonic
            ? constantFunctions.maskName(params.value ?? "")
            : params.value ?? "",
      },
      {
        headerName: "TPV USD",
        field: "TV_USD_Success",
        flex: 0.9,
        minWidth: 90,
        maxWidth: 120,
        valueFormatter: (params) =>
          constantFunctions.formatCurrency(Number(params.value)),
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Success %",
        field: "successRatePercent",
        flex: 1.0,
        minWidth: 100,
        maxWidth: 130,
        valueFormatter: (params) => `${params.value}%`,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "# Trades",
        field: "totalTrades",
        flex: 0.9,
        minWidth: 90,
        maxWidth: 120,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "In-Progress",
        field: "inProgressCount",
        flex: 1.0,
        minWidth: 100,
        maxWidth: 130,
        valueFormatter: (params) => params.value ?? 0,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
    ];

    return isPaysonic
      ? columns.filter((col) => col.field !== "TV_USD_Success")
      : columns;
  }, [constantFunctions, isPaysonic]);

  const upiColumnDefs: ColDef[] = useMemo(() => {
    const columns: ColDef[] = [
      {
        headerName: "Date",
        field: "slot",
        flex: 0.7,
        minWidth: 70,
        maxWidth: 100,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agDateColumnFilter",
        valueFormatter: (params) =>
          constantFunctions.dateFormatInDDMMYY(params.value ?? ""),
      },
      {
        headerName: "Name",
        field: "name",
        flex: 0.9,
        minWidth: 90,
        maxWidth: 130,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
        },
      },
      {
        headerName: "UPI ID",
        field: "upiId",
        flex: 1.0,
        minWidth: 100,
        maxWidth: 150,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
        },
      },
      {
        headerName: "User ID",
        field: "userId",
        flex: 0.8,
        minWidth: 80,
        maxWidth: 120,
        cellRenderer: (params: any) => {
          const { data } = params;
          return (
            <div className="flex items-center justify-between h-full gap-1">
              <span className="text-[8px] text-text-base font-medium truncate">
                {data.userId}
              </span>
              <CopyButton
                copyText={data.userId.toString()}
                copyMessage="User ID copied!"
                copyIconSize={10}
                buttonSize="12px"
                borderRadius="2px"
              />
            </div>
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Wallet",
        field: "cashierWalletBal",
        flex: 0.8,
        minWidth: 80,
        maxWidth: 110,
        valueFormatter: (params) =>
          `$${constantFunctions.formatCurrency(Number(params.value) ?? 0)}`,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "TPV USD",
        field: "TV_USD_Success",
        flex: 0.9,
        minWidth: 90,
        maxWidth: 120,
        valueFormatter: (params) =>
          constantFunctions.formatCurrency(Number(params.value)),
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Success %",
        field: "successRatePercent",
        flex: 1.0,
        minWidth: 100,
        maxWidth: 130,
        valueFormatter: (params) => `${params.value}%`,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "# Trades",
        field: "totalTrades",
        flex: 0.9,
        minWidth: 90,
        maxWidth: 120,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "In-Progress",
        field: "inProgressCount",
        flex: 1.1,
        minWidth: 110,
        maxWidth: 140,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
    ];

    return isPaysonic
      ? columns.filter((col) => col.field !== "TV_USD_Success")
      : columns;
  }, [constantFunctions, isPaysonic]);

  const exportSystemDataToCsv = async () => {
    try {
      setSystemExportLoading(true);

      const { MAX_ROWS, ROWS_PER_PAGE, TOTAL_PAGES } = CSV_EXPORT_CONSTANTS;
      let allSystemData: GetSystemSuccessRateResponseData[] = [];

      for (let i = 1; i <= TOTAL_PAGES; i++) {
        const payload: GetSystemSuccessRatePayload = {
          page: i,
          rows: ROWS_PER_PAGE,
          groupBy:
            dailyOrHourlySystemSuccessRate === "Daily" ? undefined : "hour",
        };

        const response = await analyticsApis.getSystemSuccessRate(payload);
        allSystemData = [...allSystemData, ...response.data];

        if (
          response.data.length < ROWS_PER_PAGE ||
          allSystemData.length >= MAX_ROWS
        ) {
          break;
        }
      }

      allSystemData = allSystemData.slice(0, MAX_ROWS);

      const headers = isPaysonic
        ? ["Date", "# Trades", "In-Progress", "Success %"]
        : ["Date", "# Trades", "In-Progress", "TPV USD", "Success %"];

      let csvContent = headers.join(",") + "\n";

      allSystemData.forEach((item) => {
        const row = isPaysonic
          ? [
              constantFunctions.dateFormatInDDMMYY(item.slot ?? ""),
              item.totalTrades,
              item.inProgressCount || 0,
              item.successRatePercent + "%",
            ]
          : [
              constantFunctions.dateFormatInDDMMYY(item.slot ?? ""),
              item.totalTrades,
              item.inProgressCount || 0,
              Number(item.TV_USD_Success),
              item.successRatePercent + "%",
            ];

        csvContent += row.join(",") + "\n";
      });

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);

      const date = new Date();
      const formattedDate = `${date.getFullYear()}-${String(
        date.getMonth() + 1,
      ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

      link.setAttribute(
        "download",
        `system-success-rate-${dailyOrHourlySystemSuccessRate.toLowerCase()}-${formattedDate}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to export system data", error);
      setError("Failed to export system data");
    } finally {
      setSystemExportLoading(false);
    }
  };

  const exportMerchantDataToCsv = async () => {
    try {
      setMerchantExportLoading(true);

      const { MAX_ROWS, ROWS_PER_PAGE, TOTAL_PAGES } = CSV_EXPORT_CONSTANTS;
      let allMerchantData: GetMerchantSuccessRateResponseData[] = [];

      for (let i = 1; i <= TOTAL_PAGES; i++) {
        const payload: GetMerchantSuccessRatePayload = {
          page: i,
          rows: ROWS_PER_PAGE,
          groupBy:
            dailyOrHourlyMerchantSuccessRate === "Daily" ? undefined : "hour",
          merchantName: debouncedMerchantSearchTerm,
        };

        const response = await analyticsApis.getMerchantSuccessRate(payload);
        allMerchantData = [...allMerchantData, ...response.data];

        if (
          response.data.length < ROWS_PER_PAGE ||
          allMerchantData.length >= MAX_ROWS
        ) {
          break;
        }
      }

      allMerchantData = allMerchantData.slice(0, MAX_ROWS);

      const headers = isPaysonic
        ? ["Date", "Merchant Name", "# Trades", "In-Progress", "Success %"]
        : [
            "Date",
            "Merchant Name",
            "# Trades",
            "In-Progress",
            "TPV USD",
            "Success %",
          ];

      let csvContent = headers.join(",") + "\n";

      allMerchantData.forEach((item) => {
        const merchantName = isPaysonic
          ? constantFunctions.maskName(item.merchantName || "")
          : item.merchantName || "";
        const escapedMerchantName = `"${merchantName.replace(/"/g, '""')}"`;

        const row = isPaysonic
          ? [
              constantFunctions.dateFormatInDDMMYY(item.slot ?? ""),
              escapedMerchantName,
              item.totalTrades,
              item.inProgressCount || 0,
              item.successRatePercent + "%",
            ]
          : [
              constantFunctions.dateFormatInDDMMYY(item.slot ?? ""),
              escapedMerchantName,
              item.totalTrades,
              item.inProgressCount || 0,
              Number(item.TV_USD_Success),
              item.successRatePercent + "%",
            ];

        csvContent += row.join(",") + "\n";
      });

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);

      const date = new Date();
      const formattedDate = `${date.getFullYear()}-${String(
        date.getMonth() + 1,
      ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

      link.setAttribute(
        "download",
        `merchant-success-rate-${dailyOrHourlyMerchantSuccessRate.toLowerCase()}-${formattedDate}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to export merchant data", error);
      setError("Failed to export merchant data");
    } finally {
      setMerchantExportLoading(false);
    }
  };

  const exportUpiDataToCsv = async () => {
    try {
      setUpiExportLoading(true);

      const { MAX_ROWS, ROWS_PER_PAGE, TOTAL_PAGES } = CSV_EXPORT_CONSTANTS;
      let allUpiData: GetUpiSuccessRateResponseData[] = [];

      for (let i = 1; i <= TOTAL_PAGES; i++) {
        const payload: GetUpiSuccessRatePayload = {
          page: i,
          rows: ROWS_PER_PAGE,
          groupBy: dailyOrHourlyUpiSuccessRate === "Daily" ? undefined : "hour",
          search: debouncedUpiSearchTerm,
        };

        const response = await analyticsApis.getUpiSuccessRate(payload);
        allUpiData = [...allUpiData, ...response.data];

        if (
          response.data.length < ROWS_PER_PAGE ||
          allUpiData.length >= MAX_ROWS
        ) {
          break;
        }
      }

      allUpiData = allUpiData.slice(0, MAX_ROWS);

      const headers = isPaysonic
        ? [
            "Date",
            "Name",
            "UPI ID",
            "User ID",
            "# Trades",
            "In-Progress",
            "Success %",
          ]
        : [
            "Date",
            "Name",
            "UPI ID",
            "User ID",
            "# Trades",
            "In-Progress",
            "TPV USD",
            "Success %",
          ];

      let csvContent = headers.join(",") + "\n";

      allUpiData.forEach((item) => {
        const row = isPaysonic
          ? [
              constantFunctions.dateFormatInDDMMYY(item.slot ?? ""),
              `"${(item.name || "").replace(/"/g, '""')}"`,
              `"${(item.upiId || "").replace(/"/g, '""')}"`,
              item.userId,
              item.totalTrades,
              item.inProgressCount || 0,
              item.successRatePercent + "%",
            ]
          : [
              constantFunctions.dateFormatInDDMMYY(item.slot ?? ""),
              `"${(item.name || "").replace(/"/g, '""')}"`,
              `"${(item.upiId || "").replace(/"/g, '""')}"`,
              item.userId,
              item.totalTrades,
              item.inProgressCount || 0,
              Number(item.TV_USD_Success),
              item.successRatePercent + "%",
            ];

        csvContent += row.join(",") + "\n";
      });

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);

      const date = new Date();
      const formattedDate = `${date.getFullYear()}-${String(
        date.getMonth() + 1,
      ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

      link.setAttribute(
        "download",
        `upi-success-rate-${dailyOrHourlyUpiSuccessRate.toLowerCase()}-${formattedDate}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to export UPI data", error);
      setError("Failed to export UPI data");
    } finally {
      setUpiExportLoading(false);
    }
  };

  const getSystemSuccessRate = async () => {
    try {
      setIsLoading(true);
      const payload: GetSystemSuccessRatePayload = {
        page: 1,
        rows: rowsPerPage,
        groupBy:
          dailyOrHourlySystemSuccessRate === "Daily" ? undefined : "hour",
      };
      const response = await analyticsApis.getSystemSuccessRate(payload);
      setSystemSuccessRateData(response.data || []);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Something went wrong";
      setError(errorMessage);
    }
  };

  const getMerchantSuccessRate = async () => {
    try {
      setIsLoading(true);
      const payload: GetMerchantSuccessRatePayload = {
        page: 1,
        rows: rowsPerPage,
        groupBy:
          dailyOrHourlyMerchantSuccessRate === "Daily" ? undefined : "hour",
        merchantName: debouncedMerchantSearchTerm,
      };
      const response = await analyticsApis.getMerchantSuccessRate(payload);
      setMerchantSuccessRateData(response.data || []);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Something went wrong";
      setError(errorMessage);
    }
  };

  const getUpiSuccessRate = async () => {
    try {
      setIsLoading(true);
      const payload: GetUpiSuccessRatePayload = {
        page: 1,
        rows: rowsPerPage,
        groupBy: dailyOrHourlyUpiSuccessRate === "Daily" ? undefined : "hour",
        search: debouncedUpiSearchTerm,
      };
      const response = await analyticsApis.getUpiSuccessRate(payload);
      setUpiSuccessRateData(response.data || []);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Something went wrong";
      setError(errorMessage);
    }
  };

  const merchantSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const upiSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (merchantSearchTimeoutRef.current) {
      clearTimeout(merchantSearchTimeoutRef.current);
    }

    merchantSearchTimeoutRef.current = setTimeout(() => {
      setDebouncedMerchantSearchTerm(merchantSearchTerm);
    }, 500);

    return () => {
      if (merchantSearchTimeoutRef.current) {
        clearTimeout(merchantSearchTimeoutRef.current);
      }
    };
  }, [merchantSearchTerm]);

  useEffect(() => {
    if (upiSearchTimeoutRef.current) {
      clearTimeout(upiSearchTimeoutRef.current);
    }

    upiSearchTimeoutRef.current = setTimeout(() => {
      setDebouncedUpiSearchTerm(upiSearchTerm);
    }, 500);

    return () => {
      if (upiSearchTimeoutRef.current) {
        clearTimeout(upiSearchTimeoutRef.current);
      }
    };
  }, [upiSearchTerm]);

  const handleMerchantSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    setMerchantSearchTerm(value);
  };

  const handleUpiSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUpiSearchTerm(value);
  };

  useEffect(() => {
    setSystemSuccessRateData([]);
    getSystemSuccessRate();
  }, [dailyOrHourlySystemSuccessRate]);

  useEffect(() => {
    setMerchantSuccessRateData([]);
    getMerchantSuccessRate();
  }, [dailyOrHourlyMerchantSuccessRate, debouncedMerchantSearchTerm]);

  useEffect(() => {
    setUpiSuccessRateData([]);
    getUpiSuccessRate();
  }, [dailyOrHourlyUpiSuccessRate, debouncedUpiSearchTerm]);

  return (
    <div className="flex flex-col h-screen">
      {/* Header */}
      <div className="h-[56px] border-b border-border-subtle bg-bg-page-body flex flex-row sticky top-0 bg-white z-10 px-[16px]">
        <p className="flex text-text-base text-[14px] font-semibold items-center">
          Analytics
        </p>
      </div>

      <div className="flex flex-col overflow-y-auto pb-[16px]">
        {/* System Success Rate Section */}
        <div className="flex flex-col w-full px-[12px] pt-[12px]">
          <div className="flex flex-row justify-between items-center mb-[12px] flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span
                className={`text-[12px] font-semibold cursor-pointer ${
                  selectedGraph !== "system"
                    ? "text-text-base"
                    : "text-text-button-tertiary"
                }`}
                onClick={() => {
                  setSelectedGraph("system");
                }}
              >
                System Success (%)
              </span>
              <div
                className="cursor-pointer flex items-center"
                onClick={exportSystemDataToCsv}
              >
                <span
                  className={`material-symbols-outlined ${
                    systemExportLoading ? "animate-spin" : ""
                  } text-[18px] text-text-button-tertiary hover:bg-bg-button-secondary rounded p-1`}
                >
                  {systemExportLoading ? "progress_activity" : "download"}
                </span>
              </div>
            </div>
            <div className="flex space-x-2 flex-wrap">
              {["Daily", "Hourly"].map((type) => (
                <div
                  key={type}
                  onClick={() => setDailyOrHourlySystemSuccessRate(type)}
                  className={`cursor-pointer border py-[4px] px-[12px] text-text-base text-[10px] font-medium rounded-[100px] ${
                    dailyOrHourlySystemSuccessRate === type
                      ? "border-border-button-secondary bg-bg-button-secondary"
                      : "border-border-subtle bg-bg-page-body"
                  }`}
                >
                  {type}
                </div>
              ))}
            </div>
          </div>
          <div className="w-full overflow-x-auto" style={{ height: "350px" }}>
            {isLoading &&
            (!systemSuccessRateData || systemSuccessRateData.length === 0) ? (
              <AgGridSkeleton
                columnDefs={systemColumnDefs}
                style={{ height: "100%", width: "100%" }}
              />
            ) : (
              <AgGridReactTable
                rowData={systemSuccessRateData || []}
                columnDefs={systemColumnDefs}
                compact={true}
              />
            )}
          </div>
        </div>

        {/* Merchant Success Rate Section */}
        <div className="flex flex-col w-full px-[12px] pt-[16px]">
          <div className="flex flex-row justify-between items-center mb-[8px] flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span
                className={`text-[12px] font-semibold cursor-pointer ${
                  selectedGraph !== "merchant"
                    ? "text-text-base"
                    : "text-text-button-tertiary"
                }`}
                onClick={() => {
                  setSelectedGraph("merchant");
                }}
              >
                Merchant Success (%)
              </span>
              <div
                className="cursor-pointer flex items-center"
                onClick={exportMerchantDataToCsv}
              >
                <span
                  className={`material-symbols-outlined ${
                    merchantExportLoading ? "animate-spin" : ""
                  } text-[18px] text-text-button-tertiary hover:bg-bg-button-secondary rounded p-1`}
                >
                  {merchantExportLoading ? "progress_activity" : "download"}
                </span>
              </div>
            </div>
            <div className="flex space-x-2 flex-wrap">
              {["Daily", "Hourly"].map((type) => (
                <div
                  key={type}
                  onClick={() => setDailyOrHourlyMerchantSuccessRate(type)}
                  className={`cursor-pointer border py-[4px] px-[12px] text-text-base text-[10px] font-medium rounded-[100px] ${
                    dailyOrHourlyMerchantSuccessRate === type
                      ? "border-border-button-secondary bg-bg-button-secondary"
                      : "border-border-subtle bg-bg-page-body"
                  }`}
                >
                  {type}
                </div>
              ))}
            </div>
          </div>
          <div className="w-full mb-[8px]">
            <GlobalTextInputField
              value={merchantSearchTerm}
              onChange={handleMerchantSearchChange}
              placeholder="Search"
              prefixIcon={
                <span className="material-symbols-outlined text-text-subtle">
                  search
                </span>
              }
              className={`px-2 py-[4px] w-full`}
            />
          </div>
          <div className="w-full overflow-x-auto" style={{ height: "350px" }}>
            {isLoading &&
            (!merchantSuccessRateData ||
              merchantSuccessRateData.length === 0) ? (
              <AgGridSkeleton
                columnDefs={merchantColumnDefs}
                style={{ height: "100%", width: "100%" }}
              />
            ) : (
              <AgGridReactTable
                rowData={merchantSuccessRateData || []}
                columnDefs={merchantColumnDefs}
                compact={true}
              />
            )}
          </div>
        </div>

        {/* UPI Success Rate Section */}
        <div className="flex flex-col w-full px-[12px] pt-[16px]">
          <div className="flex flex-row justify-between items-center mb-[8px] flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span
                className={`text-[12px] font-semibold cursor-pointer ${
                  selectedGraph !== "upi"
                    ? "text-text-base"
                    : "text-text-button-tertiary"
                }`}
                onClick={() => {
                  setSelectedGraph("upi");
                }}
              >
                UPI ID Success (%)
              </span>
              <div
                className="cursor-pointer flex items-center"
                onClick={exportUpiDataToCsv}
              >
                <span
                  className={`material-symbols-outlined ${
                    upiExportLoading ? "animate-spin" : ""
                  } text-[18px] text-text-button-tertiary hover:bg-bg-button-secondary rounded p-1`}
                >
                  {upiExportLoading ? "progress_activity" : "download"}
                </span>
              </div>
            </div>
            <div className="flex space-x-2 flex-wrap">
              {["Daily", "Hourly"].map((type) => (
                <div
                  key={type}
                  onClick={() => setDailyOrHourlyUpiSuccessRate(type)}
                  className={`cursor-pointer border py-[4px] px-[12px] text-text-base text-[10px] font-medium rounded-[100px] ${
                    dailyOrHourlyUpiSuccessRate === type
                      ? "border-border-button-secondary bg-bg-button-secondary"
                      : "border-border-subtle bg-bg-page-body"
                  }`}
                >
                  {type}
                </div>
              ))}
            </div>
          </div>
          <div className="w-full mb-[8px]">
            <GlobalTextInputField
              value={upiSearchTerm}
              onChange={handleUpiSearchChange}
              placeholder="Search UPI"
              prefixIcon={
                <span className="material-symbols-outlined text-text-subtle">
                  search
                </span>
              }
              className={`px-2 py-[4px] w-full`}
            />
          </div>
          <div className="w-full overflow-x-auto" style={{ height: "350px" }}>
            {isLoading &&
            (!upiSuccessRateData || upiSuccessRateData.length === 0) ? (
              <AgGridSkeleton
                columnDefs={upiColumnDefs}
                style={{ height: "100%", width: "100%" }}
              />
            ) : (
              <AgGridReactTable
                rowData={upiSuccessRateData || []}
                columnDefs={upiColumnDefs}
                compact={true}
              />
            )}
          </div>
        </div>

        {/* Chart Section */}
        <div className="flex w-full mt-[16px] px-[12px] overflow-hidden">
          <div className="flex w-full h-[300px]">
            {selectedGraph === "system" &&
              systemSuccessRateData &&
              systemSuccessRateData?.length > 0 && (
                <SystemSuccessRateChart data={systemSuccessRateData} />
              )}
            {selectedGraph === "merchant" &&
              merchantSuccessRateData &&
              merchantSuccessRateData?.length > 0 && (
                <MerchantSuccessRateChart data={merchantSuccessRateData} />
              )}
            {selectedGraph === "upi" &&
              upiSuccessRateData &&
              upiSuccessRateData?.length > 0 && (
                <UpiSuccessRateChart data={upiSuccessRateData} />
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileAnalytics;
